import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ViDetails, ViPartnerInfo } from 'pco-product-service-internal-consumer';
import { getVideoIdentificationDetails } from 'services/video-identification/getVideoIdentificationDetails';
import { getVideoIdentificationData } from 'services/video-identification/getVideoIdentificationData';
import { PcoTabNames, setShownPage } from 'reducers/shownPageReducer';
import { closeModal, openModal } from 'reducers/modalReducer';

export type VideoIdentificationSliceType = {
  videoIdentificationData?: ViPartnerInfo;
  videoIdentificationDetails?: ViDetails;
  isLoading: boolean;
};

export const initialVideoIdentificationState: VideoIdentificationSliceType = {
  isLoading: false,
};

export const fetchVideoIdentificationData = createAsyncThunk(
  'VIDEO_IDENTIFICATION/fetchVideoIdentificationData',
  async (partnerNumber: string, thunkAPI) => {
    try {
      const data = await getVideoIdentificationData(partnerNumber);
      // we dispatch the data here instead letting the 'fulfilled' thunk dispatch it
      //  so it runs  before dispatching  the tab change action
      thunkAPI.dispatch(setVideoIdentificationData(data));
      thunkAPI.dispatch(setShownPage(PcoTabNames.VideoIdentificationForm));
      return;
    } catch (err: unknown) {
      thunkAPI.dispatch(
        openModal({
          modalType: 'JBErrorModalWrapper',
          modalProps: {
            httpError: err,
            okHandler: () => {
              thunkAPI.dispatch(closeModal());
            },
            technicalInfo: {
              title: 'Error when retrieving Video Identification information',
              userErrorMessage:
                'An error occurred when retrieving Video Identification information. Please try again later.',
            },
          },
        }),
      );
      return 'error';
    }
  },
);

export const fetchVideoIdentificationDetails = createAsyncThunk(
  'VIDEO_IDENTIFICATION/fetchVideoIdentificationDetails',
  async (serviceAgreementId: string, thunkAPI) => {
    try {
      return await getVideoIdentificationDetails(serviceAgreementId);
    } catch (err: unknown) {
      thunkAPI.dispatch(
        openModal({
          modalType: 'JBErrorModalWrapper',
          modalProps: {
            httpError: err,
            okHandler: () => {
              thunkAPI.dispatch(closeModal());
            },
            technicalInfo: {
              title: 'Error when retrieving Video Identification information',
              userErrorMessage:
                'An error occurred when retrieving Video Identification information. Please try again later.',
            },
          },
        }),
      );
      return 'error';
    }
  },
);

const videoIdentificationSlice = createSlice({
  name: 'VIDEO_IDENTIFICATION',
  initialState: initialVideoIdentificationState,
  reducers: {
    setVideoIdentificationData: (state, action: PayloadAction<ViPartnerInfo>) => {
      return { ...state, videoIdentificationData: action.payload };
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchVideoIdentificationData.pending, (state) => {
      return { ...state, isLoading: true };
    });
    builder.addCase(fetchVideoIdentificationData.fulfilled, (state) => {
      return { ...state, isLoading: false };
    });
    builder.addCase(fetchVideoIdentificationData.rejected, (state) => {
      return { ...state, isLoading: false };
    });
    builder.addCase(fetchVideoIdentificationDetails.pending, (state) => {
      return { ...state, isLoading: true };
    });
    builder.addCase(fetchVideoIdentificationDetails.fulfilled, (state, action) => {
      return {
        ...state,
        videoIdentificationDetails:
          typeof action.payload === 'string' ? state.videoIdentificationDetails : action.payload,
        isLoading: false,
      };
    });
    builder.addCase(fetchVideoIdentificationDetails.rejected, (state) => {
      return { ...state, isLoading: false };
    });
  },
});

export const { setVideoIdentificationData } = videoIdentificationSlice.actions;
export default videoIdentificationSlice.reducer;
